import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { CheckCircle, Trophy } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useSoundEffects } from '../../../hooks/cabin/useSoundEffects';
import { useConfetti } from '../../../hooks/cabin/useConfetti';

function shuffleArray(arr) {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

export default function MatchPairs({
  phase,
  onPhaseComplete,
  // checkAnswer prop kabul edilir ama MatchPairs local grading guvenilir
  // (pairIndex eslesmesi strip edilmeyen pairs uzerinden yapilir).
  checkAnswer, // eslint-disable-line no-unused-vars
  'data-testid': testId = 'match-pairs',
}) {
  const { t } = useLanguage();
  const { play } = useSoundEffects();
  const { fireSmall } = useConfetti();
  const shouldReduce = useReducedMotion();

  // Stabilize pairs reference: phase.content.pairs is a static prop, but
  // `|| []` creates a new array every render and would cause useMemo/useCallback
  // to re-run unnecessarily. Use the raw value and default inside useMemo.
  const rawPairs = phase?.content?.pairs;
  const maxXP = phase?.xp_in_phase || 0;

  /**
   * Backend sends resolved strings: pairs[i] = { left, right }
   * Legacy fallback: left_tr / left_en / right_tr / right_en
   */
  const leftItems = useMemo(() => {
    const pairs = rawPairs || [];
    return pairs.map((p, i) => ({
      id: `left_${i}`,
      pairIndex: i,
      text: p.left || p.left_tr || p.left_en || '',
    }));
  }, [rawPairs]);

  const rightItems = useMemo(() => {
    const pairs = rawPairs || [];
    return shuffleArray(pairs.map((p, i) => ({
      id: `right_${i}`,
      pairIndex: i,
      text: p.right || p.right_tr || p.right_en || '',
    })));
  }, [rawPairs]);

  const totalPairs = leftItems.length;

  const [selectedLeft, setSelectedLeft] = useState(null);
  const [selectedRight, setSelectedRight] = useState(null);
  const [matched, setMatched] = useState({}); // { leftId: rightId }
  const [shakeId, setShakeId] = useState(null);
  const [completed, setCompleted] = useState(false);

  const correctMatchCount = Object.keys(matched).length;

  const handleLeftClick = useCallback((item) => {
    if (matched[item.id] || completed) return;
    play('click');
    setSelectedLeft(item);
    setSelectedRight(null);
  }, [matched, completed, play]);

  const handleRightClick = useCallback((item) => {
    if (!selectedLeft || completed) return;
    if (Object.values(matched).includes(item.id)) return;

    play('click');
    setSelectedRight(item);

    const isCorrect = selectedLeft.pairIndex === item.pairIndex;

    if (isCorrect) {
      play('correct');
      if (!shouldReduce) fireSmall({ y: 0.5 });

      setMatched((prev) => {
        const newMatched = { ...prev, [selectedLeft.id]: item.id };
        if (Object.keys(newMatched).length === totalPairs) {
          setCompleted(true);
          setTimeout(() => {
            const score = maxXP;
            // Tum eslesmeler dogru (yanlis eslesme kaydedilmez). Her cift icin
            // left_index === right_index (pairIndex) gonderilir → server grading.
            const structuredAnswers = leftItems.map((li) => ({
              left_index: li.pairIndex,
              right_index: li.pairIndex,
            }));
            onPhaseComplete({
              score,
              maxScore: maxXP,
              answers: structuredAnswers, // [{left_index, right_index}]
              timeSpent: 0,
            });
          }, 1200);
        }
        return newMatched;
      });
      setSelectedLeft(null);
      setSelectedRight(null);
    } else {
      play('wrong');
      setShakeId(item.id);
      setTimeout(() => {
        setShakeId(null);
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 600);
    }
  }, [selectedLeft, matched, completed, play, shouldReduce, fireSmall, totalPairs, maxXP, onPhaseComplete, leftItems]);

  const getLeftState = (item) => {
    if (matched[item.id]) return 'matched';
    if (selectedLeft?.id === item.id) return 'selected';
    return 'idle';
  };

  const getRightState = (item) => {
    if (Object.values(matched).includes(item.id)) return 'matched';
    if (selectedRight?.id === item.id) return 'selected';
    return 'idle';
  };

  const itemClasses = {
    idle: 'border-2 border-[var(--border-light)] bg-white text-[var(--text-main)] hover:border-[var(--brand-primary)] hover:bg-sky-50',
    selected: 'border-2 border-[var(--brand-primary)] bg-sky-50 text-sky-700 font-semibold',
    matched: 'border-2 border-[#10B981] bg-[#10B981]/10 text-emerald-700 font-semibold cursor-default',
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-2xl mx-auto" data-testid={testId}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-[var(--text-muted)]">
          {t('exercise.match_instruction') || 'Sol ve sağ taraftan eşleşenlere tıklayın'}
        </p>
        <span className="text-sm font-semibold text-[var(--brand-primary)]">
          {correctMatchCount}/{totalPairs}
        </span>
      </div>

      {/* Two columns */}
      <div
        className="grid grid-cols-2 gap-4"
        role="application"
        aria-label={t('exercise.match_aria_label') || 'Eşleştirme egzersizi'}
      >
        {/* Left column */}
        <div className="flex flex-col gap-2">
          {leftItems.map((item) => {
            const state = getLeftState(item);
            return (
              <motion.button
                key={item.id}
                whileHover={state === 'matched' || shouldReduce ? {} : { scale: 1.01 }}
                whileTap={state === 'matched' || shouldReduce ? {} : { scale: 0.99 }}
                onClick={() => handleLeftClick(item)}
                disabled={state === 'matched'}
                aria-pressed={state === 'selected'}
                aria-label={`${item.text}${state === 'matched' ? ` — ${t('exercise.matched') || 'eşleşti'}` : ''}`}
                className={`w-full px-4 py-3 rounded-xl text-sm text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${itemClasses[state]}`}
                data-testid={`${testId}-left-${item.id}`}
              >
                <div className="flex items-center gap-2">
                  {state === 'matched' && (
                    <CheckCircle size={14} className="text-[#10B981] shrink-0" aria-hidden="true" />
                  )}
                  <span className="flex-1">{item.text}</span>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-2">
          {rightItems.map((item) => {
            const state = getRightState(item);
            const isShaking = shakeId === item.id;

            return (
              <motion.button
                key={item.id}
                animate={isShaking && !shouldReduce ? { x: [-6, 6, -6, 6, 0] } : { x: 0 }}
                transition={{ duration: 0.4 }}
                whileHover={state === 'matched' || shouldReduce ? {} : { scale: 1.01 }}
                whileTap={state === 'matched' || shouldReduce ? {} : { scale: 0.99 }}
                onClick={() => handleRightClick(item)}
                disabled={state === 'matched' || !selectedLeft}
                aria-pressed={state === 'selected'}
                aria-label={`${item.text}${state === 'matched' ? ` — ${t('exercise.matched') || 'eşleşti'}` : ''}`}
                className={`w-full px-4 py-3 rounded-xl text-sm text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)] ${
                  !selectedLeft && state === 'idle' ? 'opacity-60' : ''
                } ${isShaking ? 'border-2 border-[var(--brand-danger)] bg-[var(--brand-danger)]/10' : itemClasses[state]}`}
                data-testid={`${testId}-right-${item.id}`}
              >
                <div className="flex items-center gap-2">
                  {state === 'matched' && (
                    <CheckCircle size={14} className="text-[#10B981] shrink-0" aria-hidden="true" />
                  )}
                  <span className="flex-1">{item.text}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Complete state */}
      <AnimatePresence>
        {completed && (
          <motion.div
            key="complete"
            initial={shouldReduce ? {} : { opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', damping: 18, stiffness: 200 }}
            className="flex items-center justify-center gap-3 p-4 bg-[#10B981]/10 border-2 border-[#10B981] rounded-xl"
            role="alert"
            data-testid={`${testId}-complete`}
          >
            <Trophy size={24} className="text-[#10B981]" aria-hidden="true" />
            <p className="font-bold text-emerald-700">
              {t('exercise.all_matched') || 'Tüm eşleşmeler tamamlandı!'}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
